import clsx from 'clsx';
import { forwardRef, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useInterval } from 'usehooks-ts';
import { getRange } from '../shared/util';

export type DotAnimation = {
  delay: number;
  duration: number;
  className: string;
};

type DottedBackgroundProps = {
  xCount: number;
  yCount: number;
  dotClassName: string;
  className?: string;
  columnContainerClassName?: string;
  resetAnimOnEnd?: boolean;
  style?: React.CSSProperties;
};

type DottedBackgroundWithAnimationProps = DottedBackgroundProps & {
  repeatInterval?: number;
  coordinatedDotAnimations?: Record<string, DotAnimation> | null; // string will be 'x-y' coordinate
};

const Dot = ({
  className,
  dotAnimation,
  repeatInterval,
  resetAnimOnEnd,
}: {
  className?: string;
  dotAnimation?: DotAnimation;
  repeatInterval?: number;
  resetAnimOnEnd?: boolean;
}) => {
  const [animation, setAnimation] = useState<DotAnimation | null>();

  useInterval(() => {
    setAnimation(dotAnimation);
  }, repeatInterval ?? null);

  useEffect(() => {
    if (!repeatInterval) {
      setAnimation(dotAnimation);
    }
  }, [dotAnimation, repeatInterval]);

  return (
    <div
      className={clsx('w-1.5 h-1.5 rounded-[3px]', animation?.className, className)}
      style={
        animation
          ? {
              animationDelay: `${animation?.delay}ms`,
              animationDuration: `${animation?.duration}ms`,
            }
          : undefined
      }
      onAnimationEnd={() => {
        resetAnimOnEnd && setAnimation(null);
      }}
    />
  );
};

const DottedBackground = forwardRef<HTMLDivElement, DottedBackgroundWithAnimationProps>(
  (
    {
      xCount,
      yCount,
      dotClassName: colorClassName,
      coordinatedDotAnimations,
      repeatInterval,
      className,
      columnContainerClassName,
      resetAnimOnEnd = true,
      style,
    },
    ref
  ) => {
    return (
      <div ref={ref} className={twMerge(clsx('w-full h-fit', className))} style={style}>
        <div className={clsx('flex gap-5', columnContainerClassName)}>
          {getRange(xCount).map((x) => (
            <div className="flex gap-5 flex-col shrink-0" key={x}>
              {getRange(yCount).map((y) => (
                <Dot
                  dotAnimation={coordinatedDotAnimations?.[`${x}-${y}`]}
                  key={y}
                  resetAnimOnEnd={resetAnimOnEnd}
                  className={colorClassName}
                  repeatInterval={repeatInterval}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }
);
export default DottedBackground;
